import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { queryClient } from '@/application/queryClient'
import type { User } from '@/domains/types'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (user: User, accessToken: string, refreshToken: string) => void
  logout: () => void
  setUser: (user: User | null) => void
  updateTokens: (accessToken: string, refreshToken: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (setState, getState) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: (user, accessToken, refreshToken) =>
        setState({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () => {
        const { isAuthenticated, accessToken, refreshToken } = getState()
        const hadSession = isAuthenticated || accessToken !== null || refreshToken !== null
        setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        })
        // Auth-dependent queries (e.g. investigations/:id/read, which returns
        // unpublished content to a privileged viewer) don't encode auth state
        // in their cache key, so a stale entry would otherwise keep serving
        // privileged data after logout until a hard page reload. `resetQueries`
        // puts every active query straight back into a loading state and
        // triggers that refetch immediately, propagating the result to any
        // still-mounted component. Note: `clear()` must NOT be called here —
        // it unsubscribes existing observers, which breaks the very refetch
        // `resetQueries` is meant to trigger for a component still on screen.
        // Skipped when there was no session: there is nothing privileged to
        // purge, and since the API client calls logout() on any 401, resetting
        // would refetch every active query, and the next anonymous 401 would
        // log out and reset again, in an endless loop.
        if (hadSession) void queryClient.resetQueries()
      },

      setUser: (user) =>
        setState({
          user,
          isAuthenticated: !!user,
        }),

      updateTokens: (accessToken, refreshToken) => setState({ accessToken, refreshToken }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)

// Mock user for development (streamer role)
export const mockStreamerUser: User = {
  id: 'streamer-1',
  username: 'streamer',
  displayName: 'Streamer',
  role: 'streamer',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}

// Selectors
export const selectUser = (state: AuthState) => state.user
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated
export const selectAccessToken = (state: AuthState) => state.accessToken
export const selectUserRole = (state: AuthState) => state.user?.role
